import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Table, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useRecoilValue, useRecoilState } from "recoil";
import { memberState, drdIconMappingVersionState, ilJsLoadedState } from "../../../../../state/atoms";
import { AppContext } from "../../../../../AppContext";
import { env } from "../../../../../env/env";
import BrandKitApi, { BrandKitDrdIconDto } from "../../../../../api/BrandKitApi";
import { DRD_ATTRIBUTES, DrdAttribute } from "../drdAttributes";
import "./IconsTab.scss";

interface IconsTabProps {
  brandKitId: string;
}

interface Row extends DrdAttribute {
  mapping?: BrandKitDrdIconDto;
}

// The Timber icon sprite is cross-origin on stage (staging.mhmfun.com vs www.musthavemenus.com/
// xstage), where browsers block <use href="sprite.svg#id"> regardless of CORS. item-library already
// fetches + inlines the CORS-enabled sprite into the document (using its own host detection), so we
// reuse that via window.ItemLibrary.ensureIconSprite and reference symbols by local fragment. This
// avoids the dashboard's env paths, which are unreliable on stage.
function useIconSprite(): boolean {
  const ilLoaded = useRecoilValue(ilJsLoadedState);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready || !ilLoaded) return;
    const ensure = (window as any).ItemLibrary?.ensureIconSprite;
    if (typeof ensure !== "function") return;
    let active = true;
    ensure().then(() => active && setReady(true));
    return () => {
      active = false;
    };
  }, [ilLoaded, ready]);
  return ready;
}

// Renders a mapped icon glyph (svg sprite symbol / letter / uploaded image).
function MappedIcon({ iconKey, type }: { iconKey: string; type?: string | null }) {
  const spriteReady = useIconSprite();
  const t = (type || "SVG").toUpperCase();
  if (t === "LETTER") return <span className="drd-icon-letter">{iconKey}</span>;
  if (t === "UPLOAD") {
    const src = iconKey.startsWith("http") ? iconKey : `${env.s3Path}/${iconKey}`;
    return <img className="drd-icon-img" src={src} alt="" />;
  }
  // Full external URL (rare) — reference directly; otherwise use the inlined sprite symbol.
  if (iconKey.startsWith("http")) {
    return (
      <svg className="drd-icon-svg" role="img">
        <use xlinkHref={iconKey} />
      </svg>
    );
  }
  if (!spriteReady) return <span className="drd-icon-svg" />;
  return (
    <svg className="drd-icon-svg" role="img">
      <use xlinkHref={`#${iconKey}`} />
    </svg>
  );
}

export function IconsTab({ brandKitId }: IconsTabProps) {
  const member = useRecoilValue(memberState);
  const { location } = useContext(AppContext);
  const memberId = member?.internalId || 0;
  const locationId = location?.internalId || 0;

  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState<Record<string, BrandKitDrdIconDto>>({});
  const [version, setVersion] = useRecoilState(drdIconMappingVersionState);

  // Re-fetch on mount and whenever any surface (Uploads tab / Asset Library) changes the mapping.
  useEffect(() => {
    if (!brandKitId) return;
    setLoading(true);
    BrandKitApi.getDrdIcons(brandKitId)
      .then((resp) => {
        const byAttr: Record<string, BrandKitDrdIconDto> = {};
        (resp.data || []).forEach((m) => (byAttr[m.attribute] = m));
        setMappings(byAttr);
      })
      .catch(() => message.error("Failed to load icon mapping"))
      .finally(() => setLoading(false));
  }, [brandKitId, version]);

  const rows: Row[] = useMemo(
    () =>
      [...DRD_ATTRIBUTES]
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((a) => ({ ...a, mapping: mappings[a.attribute] })),
    [mappings]
  );

  function openPicker(row: Row) {
    const picker = window.ItemLibrary?.ILIconPicker;
    if (!picker) {
      message.error("Icon library is still loading, please try again in a moment");
      return;
    }
    picker.show({
      memberId,
      locationId,
      onSelect: (icon) => saveIcon(row, icon),
    });
  }

  function saveIcon(row: Row, icon: { iconKey: string; type: string; altText: string }) {
    const dto: BrandKitDrdIconDto = {
      attribute: row.attribute,
      type: row.type,
      iconKey: icon.iconKey,
      iconType: icon.type,
      altText: icon.altText,
      hidden: false,
    };
    BrandKitApi.upsertDrdIcon(brandKitId, row.attribute, dto)
      .then((resp) => {
        setMappings((prev) => ({ ...prev, [row.attribute]: resp.data }));
        setVersion((v) => v + 1);
      })
      .catch(() => message.error("Failed to save icon"));
  }

  function clearIcon(row: Row) {
    BrandKitApi.deleteDrdIcon(brandKitId, row.attribute)
      .then(() => {
        setMappings((prev) => {
          const next = { ...prev };
          delete next[row.attribute];
          return next;
        });
        setVersion((v) => v + 1);
      })
      .catch(() => message.error("Failed to remove icon"));
  }

  const columns = [
    {
      title: "DiningRD Attribute",
      dataIndex: "label",
      key: "label",
      sorter: (a: Row, b: Row) => a.label.localeCompare(b.label),
      defaultSortOrder: "ascend" as const,
      render: (label: string) => <span className="drd-attr-label">{label}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 160,
      sorter: (a: Row, b: Row) => a.type.localeCompare(b.type),
      render: (type: string) => (
        <span className={`drd-type-badge ${type === "ALLERGEN" ? "allergen" : "tag"}`}>
          {type}
        </span>
      ),
    },
    {
      title: "Icon",
      key: "icon",
      width: 160,
      render: (_: unknown, row: Row) => {
        const mapped = row.mapping && row.mapping.iconKey;
        if (mapped) {
          return (
            <div className="drd-icon-cell mapped">
              <button className="drd-icon-mapped" onClick={() => openPicker(row)} title="Change icon">
                <MappedIcon iconKey={row.mapping!.iconKey!} type={row.mapping!.iconType} />
              </button>
              <CloseOutlined
                className="drd-icon-clear"
                title="Remove icon"
                onClick={() => clearIcon(row)}
              />
            </div>
          );
        }
        return (
          <Button className="drd-select-icon-btn" onClick={() => openPicker(row)}>
            Select Icon
          </Button>
        );
      },
    },
  ];

  if (loading) return <Skeleton active />;

  return (
    <div className="icons-tab">
      <Table
        className="drd-icons-table"
        dataSource={rows}
        columns={columns}
        rowKey="attribute"
        pagination={false}
      />
    </div>
  );
}
